import { defineStore } from 'pinia'
import {
    CognitoIdentityProviderClient,
    InitiateAuthCommand,
    RespondToAuthChallengeCommand,
    ForgotPasswordCommand,
    ConfirmForgotPasswordCommand,
    UpdateUserAttributesCommand,
    GlobalSignOutCommand,
    GetUserCommand,
} from '@aws-sdk/client-cognito-identity-provider'

// Create Cognito client
const cognitoClient = new CognitoIdentityProviderClient({
    region: import.meta.env.VITE_COGNITO_REGION,
})

const CLIENT_ID = import.meta.env.VITE_COGNITO_APP_CLIENT_ID

// Build a Cognito-style localStorage key. Pass a username to scope to a specific user.
const lsKey = (suffix, username = null) =>
    username
        ? `CognitoIdentityServiceProvider.${CLIENT_ID}.${username}.${suffix}`
        : `CognitoIdentityServiceProvider.${CLIENT_ID}.${suffix}`

export const useCognitoStore = defineStore('cognito', {
    state: () => {
        // Check localStorage for pending OTP session
        let pendingOtp = null
        try {
            const storedPendingOtp = localStorage.getItem('pendingOtp')
            if (storedPendingOtp) {
                const parsed = JSON.parse(storedPendingOtp)
                // Check if the pendingOtp is expired (older than 10 minutes, matching Cognito's OTP code expiry)
                if (parsed.createdAt && Date.now() - parsed.createdAt < 10 * 60 * 1000) {
                    pendingOtp = parsed
                } else {
                    // Clear expired pendingOtp
                    localStorage.removeItem('pendingOtp')
                }
            }
        } catch (error) {
            console.warn('Failed to parse pendingOtp from localStorage:', error)
            // Clear invalid data
            localStorage.removeItem('pendingOtp')
        }

        // Check localStorage for stored tokens using Cognito format
        let storedTokens = {
            idToken: null,
            accessToken: null,
            refreshToken: null,
        }
        let storedUser = null
        let storedEmail = null

        try {
            // Get the last authenticated user
            const lastAuthUser = localStorage.getItem(lsKey('LastAuthUser'))

            if (lastAuthUser) {
                // Get tokens
                const idToken = localStorage.getItem(lsKey('idToken', lastAuthUser))
                const accessToken = localStorage.getItem(lsKey('accessToken', lastAuthUser))
                const refreshToken = localStorage.getItem(lsKey('refreshToken', lastAuthUser))
                const userData = localStorage.getItem(lsKey('userData', lastAuthUser))

                if (idToken && accessToken && !pendingOtp) {
                    storedTokens = {
                        idToken,
                        accessToken,
                        refreshToken,
                    }
                    storedEmail = lastAuthUser

                    if (userData) {
                        storedUser = JSON.parse(userData)
                    } else {
                        // Create basic user object if userData doesn't exist
                        storedUser = {
                            username: lastAuthUser,
                        }
                    }
                }
            }
        } catch (error) {
            console.warn('Failed to parse stored authentication data from localStorage:', error)
        }

        return {
            user: storedUser,
            session: null, // Will be created dynamically from tokens
            email: pendingOtp?.email || storedEmail,
            phone: pendingOtp?.phone,
            pendingOtp, // Store session data waiting for OTP verification
            tokens: storedTokens,
        }
    },
    getters: {
        isAdmin: (state) => {
            if (!state.tokens.idToken) return false
            try {
                const payload = JSON.parse(atob(state.tokens.idToken.split('.')[1]))
                return payload['cognito:groups']?.some((g) => g === 'admin')
            } catch {
                return false
            }
        },
        isPendingOtpValid: (state) => {
            if (!state.pendingOtp || !state.pendingOtp.createdAt) return false
            const tenMinutesInMs = 10 * 60 * 1000
            return Date.now() - state.pendingOtp.createdAt < tenMinutesInMs
        },
    },
    actions: {
        // Silently refresh tokens using the stored refresh token.
        // Returns true if successful, false if refresh token is missing or expired.
        //
        // Token lifetimes (configured in Cognito User Pool app client settings):
        //   - Access/ID tokens: 1 hour — refreshed automatically by this method
        //   - Refresh token:    30 days — determines how long a user stays logged in
        //
        // Users will remain logged in for up to 30 days without being prompted,
        // as long as they use the app at least once within that window.
        async refreshTokens() {
            if (!this.tokens.refreshToken || !this.email) return false

            try {
                const command = new InitiateAuthCommand({
                    ClientId: CLIENT_ID,
                    AuthFlow: 'REFRESH_TOKEN_AUTH',
                    AuthParameters: {
                        REFRESH_TOKEN: this.tokens.refreshToken,
                    },
                })

                const response = await cognitoClient.send(command)

                if (response.AuthenticationResult) {
                    // REFRESH_TOKEN_AUTH does not return a new refresh token
                    this.setTokensFromAuthResult(
                        {
                            ...response.AuthenticationResult,
                            RefreshToken: response.AuthenticationResult.RefreshToken ?? this.tokens.refreshToken,
                        },
                        this.email
                    )
                    return true
                }

                return false
            } catch (error) {
                console.warn('Token refresh failed:', error)
                return false
            }
        },

        async authenticate() {
            // Try to get current user from stored tokens
            if (!this.tokens.accessToken) {
                throw new Error('No User')
            }

            // If tokens are expired, attempt a silent refresh before calling GetUser
            if (this.areTokensExpired()) {
                const refreshed = await this.refreshTokens()
                if (!refreshed) {
                    this.clearCognitoDataFromLocalStorage()
                    this.$reset()
                    throw new Error('No User')
                }
            }

            try {
                const command = new GetUserCommand({
                    AccessToken: this.tokens.accessToken,
                })

                const response = await cognitoClient.send(command)

                // Create user-like object for compatibility
                const user = {
                    username: response.Username,
                    attributes: response.UserAttributes.reduce((acc, attr) => {
                        acc[attr.Name] = attr.Value
                        return acc
                    }, {}),
                }

                // Create session-like object for compatibility
                const session = this.createSessionObject()

                this.$patch({
                    user,
                    session,
                })

                // Persist updated user data to localStorage
                this.persistToLocalStorage()

                return user
            } catch (error) {
                // If authentication fails, clear stored data
                this.clearCognitoDataFromLocalStorage()
                this.$reset()
                throw new Error('Authentication failed: ' + error.message, { cause: error })
            }
        },

        async setAccountAttribute(accountId) {
            if (!this.tokens.accessToken) {
                throw new Error('User not authenticated')
            }

            try {
                const command = new UpdateUserAttributesCommand({
                    AccessToken: this.tokens.accessToken,
                    UserAttributes: [
                        {
                            Name: 'custom:account',
                            Value: accountId,
                        },
                    ],
                })

                await cognitoClient.send(command)
            } catch (error) {
                throw new Error(error.message || JSON.stringify(error), { cause: error })
            }
        },

        // Initiate passwordless sign-in using EMAIL_OTP
        async initiateOtpSignIn({ email, phone, preferredChallenge = 'EMAIL_OTP' } = {}) {
            // Validation errors always surface — they are not Cognito responses
            this.validateEmail(email)

            if (preferredChallenge === 'SMS_OTP' && phone) {
                phone = this.normalizePhone(phone)
                this.validatePhone(phone)
            }

            try {
                const command = new InitiateAuthCommand({
                    ClientId: CLIENT_ID,
                    AuthFlow: 'USER_AUTH',
                    AuthParameters: {
                        USERNAME: email,
                        PREFERRED_CHALLENGE: preferredChallenge,
                    },
                })

                let response = await cognitoClient.send(command)

                // Cognito USER_AUTH flow may return SELECT_CHALLENGE first,
                // requiring us to confirm which challenge type we want.
                if (response.ChallengeName === 'SELECT_CHALLENGE') {
                    const selectCommand = new RespondToAuthChallengeCommand({
                        ClientId: CLIENT_ID,
                        ChallengeName: 'SELECT_CHALLENGE',
                        Session: response.Session,
                        ChallengeResponses: {
                            USERNAME: email,
                            ANSWER: preferredChallenge,
                        },
                    })
                    response = await cognitoClient.send(selectCommand)
                }

                if (response.ChallengeName === 'EMAIL_OTP' || response.ChallengeName === 'SMS_OTP') {
                    const pendingOtp = {
                        session: response.Session,
                        email,
                        phone,
                        challengeName: response.ChallengeName,
                        createdAt: Date.now(),
                    }

                    this.$patch({
                        pendingOtp,
                        email,
                        phone,
                        user: null,
                        session: null,
                        tokens: { idToken: null, accessToken: null, refreshToken: null },
                    })

                    // Store in localStorage
                    localStorage.setItem('pendingOtp', JSON.stringify(pendingOtp))

                    return {
                        otpRequired: true,
                        destination: response.ChallengeParameters?.CODE_DELIVERY_DESTINATION || email,
                        message: `Verification code sent to ${response.ChallengeParameters?.CODE_DELIVERY_DESTINATION || email}`,
                    }
                }

                // Unexpected challenge type returned from Cognito
                throw new Error('Sign in failed. Please try again.')
            } catch (error) {
                const errorType = error.__type || error.name || error.code || ''

                const errorMessages = {
                    LimitExceededException: 'Too many attempts. Please try again in a minute or two.',
                    TooManyRequestsException: 'Too many attempts. Please try again in a minute or two.',
                    UserNotFoundException: 'No account found for that email address.',
                    InvalidParameterException:
                        preferredChallenge === 'SMS_OTP'
                            ? 'Unable to send a code via text message. Please try email instead.' // This error can occur if account does not have a valid phone number for SMS OTP or the account does not exist at all.
                            : 'No account found for that email address.',
                    PasswordResetRequiredException:
                        'A password reset is required for this account. Please use "Forgot password" to set a new one.',
                }

                const message = errorMessages[errorType]
                if (message) throw new Error(message, { cause: error })

                // Surface any other unexpected errors
                throw new Error(error.message || 'Sign in failed. Please try again.', { cause: error })
            }
        },

        // Complete EMAIL_OTP sign-in with verification code
        async completeOtpSignIn(verificationCode) {
            if (!this.pendingOtp) {
                throw new Error('No pending OTP verification. Please initiate sign-in first.')
            }

            if (!verificationCode) {
                throw new Error('Verification code is required')
            }

            try {
                const otpCodeKey = `${this.pendingOtp.challengeName === 'SMS_OTP' ? 'SMS' : 'EMAIL'}_OTP_CODE`

                const command = new RespondToAuthChallengeCommand({
                    ClientId: CLIENT_ID,
                    ChallengeName: this.pendingOtp.challengeName,
                    Session: this.pendingOtp.session,
                    ChallengeResponses: {
                        [otpCodeKey]: verificationCode,
                        USERNAME: this.pendingOtp.email,
                    },
                })

                const response = await cognitoClient.send(command)

                if (response.AuthenticationResult) {
                    const userEmail = this.pendingOtp.email
                    this.setTokensFromAuthResult(response.AuthenticationResult, this.pendingOtp.email)
                    this.$patch({ pendingOtp: null })

                    // Clear from localStorage
                    localStorage.removeItem('pendingOtp')

                    return userEmail
                }

                throw new Error('Authentication failed')
            } catch (error) {
                throw {
                    code: error.name,
                    message: error.message || JSON.stringify(error),
                }
            }
        },

        // Legacy password-based sign-in (keep for backward compatibility)
        async signIn({ email, password }) {
            this.validateSignInPayload({ email, password })

            try {
                const command = new InitiateAuthCommand({
                    ClientId: CLIENT_ID,
                    AuthFlow: 'USER_PASSWORD_AUTH',
                    AuthParameters: {
                        USERNAME: email,
                        PASSWORD: password,
                    },
                })

                const response = await cognitoClient.send(command)

                if (response.AuthenticationResult) {
                    this.setTokensFromAuthResult(response.AuthenticationResult, email)
                    // Clear any stale OTP session so it doesn't redirect to the confirmation screen
                    this.$patch({ pendingOtp: null })
                    localStorage.removeItem('pendingOtp')
                    return
                }

                if (response.ChallengeName === 'NEW_PASSWORD_REQUIRED') {
                    throw new Error('New Password Required')
                }

                throw new Error('Authentication failed')
            } catch (error) {
                throw new Error(error.message || 'Sign in failed', { cause: error })
            }
        },

        async signOut() {
            if (!this.tokens.accessToken) return

            try {
                const command = new GlobalSignOutCommand({
                    AccessToken: this.tokens.accessToken,
                })

                await cognitoClient.send(command)
            } catch (error) {
                // Continue with local sign out even if global sign out fails
                console.warn('Global sign out failed:', error)
            } finally {
                this.clearCognitoDataFromLocalStorage()
                this.$reset()
            }
        },

        clearPendingOtp() {
            this.$patch({ pendingOtp: null })
            localStorage.removeItem('pendingOtp')
        },

        async forgotPassword(payload) {
            if (!payload.email) {
                throw new Error('Email is required')
            }

            try {
                const command = new ForgotPasswordCommand({
                    ClientId: CLIENT_ID,
                    Username: payload.email,
                })

                const response = await cognitoClient.send(command)
                return response
            } catch (error) {
                throw new Error(error.message || 'Failed to initiate password reset', { cause: error })
            }
        },

        async resetPassword(payload) {
            if (!payload.email) {
                throw new Error('Email is required')
            }

            try {
                const command = new ConfirmForgotPasswordCommand({
                    ClientId: CLIENT_ID,
                    Username: payload.email,
                    ConfirmationCode: payload.confirmationCode,
                    Password: payload.password,
                })

                const response = await cognitoClient.send(command)
                return response
            } catch (error) {
                throw new Error(error.message || 'Failed to reset password', { cause: error })
            }
        },

        // Helper method to set tokens from auth result
        setTokensFromAuthResult(authResult, email) {
            const tokens = {
                idToken: authResult.IdToken,
                accessToken: authResult.AccessToken,
                refreshToken: authResult.RefreshToken,
            }

            const user = { username: email }

            // Create session-like object for compatibility
            const session = this.createSessionObject(tokens)

            this.$patch({
                user,
                session,
                tokens,
                email,
            })

            // Persist to localStorage
            this.persistToLocalStorage()
        },

        // Helper method to persist authentication data to localStorage
        persistToLocalStorage() {
            try {
                if (this.tokens.idToken && this.tokens.accessToken && this.email) {
                    localStorage.setItem(lsKey('LastAuthUser'), this.email)
                    localStorage.setItem(lsKey('idToken', this.email), this.tokens.idToken)
                    localStorage.setItem(lsKey('accessToken', this.email), this.tokens.accessToken)
                    if (this.tokens.refreshToken) {
                        localStorage.setItem(lsKey('refreshToken', this.email), this.tokens.refreshToken)
                    }
                    if (this.user) {
                        localStorage.setItem(lsKey('userData', this.email), JSON.stringify(this.user))
                    }
                } else {
                    // Clear localStorage if no valid tokens
                    this.clearCognitoDataFromLocalStorage()
                }
            } catch (error) {
                console.warn('Failed to persist authentication data to localStorage:', error)
            }
        },

        // Helper method to clear authentication data from localStorage
        clearCognitoDataFromLocalStorage() {
            try {
                const lastAuthUser = localStorage.getItem(lsKey('LastAuthUser'))
                if (lastAuthUser) {
                    ;['idToken', 'accessToken', 'refreshToken', 'userData'].forEach((key) =>
                        localStorage.removeItem(lsKey(key, lastAuthUser))
                    )
                }
                localStorage.removeItem(lsKey('LastAuthUser'))
            } catch (error) {
                console.warn('Failed to clear authentication data from localStorage:', error)
            }
        },

        // Create session-like object for backward compatibility
        createSessionObject(customTokens = null) {
            const tokens = customTokens || this.tokens

            if (!tokens.idToken || !tokens.accessToken) {
                return null
            }

            return {
                getIdToken: () => ({
                    getJwtToken: () => tokens.idToken,
                    payload: this.parseJwtPayload(tokens.idToken),
                }),
                getAccessToken: () => ({
                    getJwtToken: () => tokens.accessToken,
                    payload: this.parseJwtPayload(tokens.accessToken),
                }),
                getRefreshToken: () => ({
                    getToken: () => tokens.refreshToken,
                }),
                isValid: () => !this.areTokensExpired(),
            }
        },

        // Helper to parse JWT payload
        parseJwtPayload(token) {
            try {
                return JSON.parse(atob(token.split('.')[1]))
            } catch {
                return {}
            }
        },

        validateEmail(email) {
            if (!email) {
                throw new Error('Email is required')
            }

            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
            if (!emailRegex.test(email)) {
                throw new Error('Valid email is required')
            }
        },

        // Normalize a phone number to E.164 format (+1XXXXXXXXXX for US numbers).
        // Accepts common formats: (555) 234-5678, 555-234-5678, 5552345678, +15552345678, etc.
        // Returns the normalized E.164 string, or throws if unrecognizable.
        normalizePhone(phone) {
            if (!phone) throw new Error('Phone number is required')

            // Already valid E.164 — pass through
            if (/^\+[1-9]\d{7,14}$/.test(phone)) return phone

            const digits = phone.replace(/\D/g, '')

            if (digits.length === 10) {
                // Bare 10-digit US number — prepend +1
                return `+1${digits}`
            } else if (digits.length === 11 && digits.startsWith('1')) {
                // 11-digit with leading 1 — prepend +
                return `+${digits}`
            }

            throw new Error('Please enter a valid phone number (e.g. (555) 234-5678 or +15552345678)')
        },

        validatePhone(phone) {
            if (!phone) {
                throw new Error('Phone number is required')
            }

            // Cognito requires E.164 format: +[country code][number], e.g. +15551234567
            // This is the only format Cognito will accept for phone_number attributes and SMS OTP.
            if (!/^\+[1-9]\d{7,14}$/.test(phone)) {
                throw new Error('Please enter a valid phone number (e.g. (555) 234-5678 or +15552345678)')
            }
        },

        validateConfirmationCode({ email, confirmationCode }) {
            if (!confirmationCode) {
                throw new Error('Confirmation Code is Required')
            }

            if (!email) {
                throw new Error('Email is required')
            }
        },

        validateSignInPayload({ email, password }) {
            this.validateEmail(email)
            this.validatePassword(password)
        },

        validatePassword(password) {
            if (!password) throw new Error('Password is required')
        },

        // Backward compatibility methods
        token() {
            return this.tokens.idToken
        },

        accessToken() {
            return this.tokens.accessToken
        },

        accessKeyId() {
            if (!this.tokens.idToken) return null
            const payload = this.parseJwtPayload(this.tokens.idToken)
            return payload['cognito:username']
        },

        createUserAndSessionFromTokens({ Username, IdToken, AccessToken, RefreshToken }) {
            const tokens = {
                idToken: IdToken,
                accessToken: AccessToken,
                refreshToken: RefreshToken,
            }

            const user = {
                username: Username,
            }

            const session = this.createSessionObject(tokens)

            this.$patch({
                user,
                session,
                tokens,
                email: Username, // Use username as email for compatibility
            })

            // Persist to localStorage
            this.persistToLocalStorage()

            return {
                user,
                session,
            }
        },

        // Returns the earliest expiry timestamp (seconds) across both tokens, or null if tokens are missing.
        getTokenExpiry() {
            if (!this.tokens.idToken || !this.tokens.accessToken) return null
            const { exp: idExp } = this.parseJwtPayload(this.tokens.idToken)
            const { exp: accessExp } = this.parseJwtPayload(this.tokens.accessToken)
            return Math.min(idExp, accessExp)
        },

        areTokensExpired() {
            const exp = this.getTokenExpiry()
            return exp === null || exp < Math.floor(Date.now() / 1000)
        },

        minutesUntilTokenExpiry() {
            const exp = this.getTokenExpiry()
            if (exp === null) return null
            const secondsLeft = exp - Math.floor(Date.now() / 1000)
            return secondsLeft > 0
                ? { minutes: Math.floor(secondsLeft / 60), seconds: secondsLeft % 60 }
                : { minutes: 0, seconds: 0 }
        },

        tokenExpiryTimeString() {
            const exp = this.getTokenExpiry()
            return exp !== null ? new Date(exp * 1000).toLocaleString() : null
        },
    },
})
